const FieldGroup = require('@/lib/CustomFields/FieldGroup');
const Membership = require('@/lib/Memberships/Membership');
const MembershipDataset = require('@/lib/Memberships/MembershipDataset');
const Redemption = require('@/lib/Redemption/Redemption');
const SeatNumber = require('@/lib/Orders/SeatNumber');
const SoldGiftVoucher = require('@/lib/GiftVouchers/SoldGiftVoucher');
const { populate } = require('@/functions/objects');
const { toInt, toNullableInt, toNullableString } = require('@/functions/transform');

/**
 * @constructor
 *
 * @param properties
 */
const ItemInstance = function (properties = {}) {
    /**
     * @type {?number}
     */
    this.ID = null;

    /**
     * The number of this instance on the line.
     * Note that this begins counting at 1 not 0!
     *
     * @type {number}
     */
    this.instance = properties.instance || 1;

    /**
     * @type {?string}
     */
    this.itemInstanceRef = properties.itemInstanceRef || null;

    /**
     * @type {?MembershipSubscription}
     */
    this.membershipSubscription = null;

    /**
     * A membership may be returned from the API as a child of the membershipSubscription for this instance.
     * We extract it here and store it as a property on ItemInstance instead of having a membership property
     * on MembershipSubscription to prevent circular references.
     *
     * @type {?Membership}
     */
    this.membership = null;

    /**
     * @type {?number}
     */
    this.netRedemptions = null;

    /**
     * @type {?OrderLine}
     */
    this.orderLine = null;

    /**
     * @type {Redemption[]}
     */
    this.redemptions = [];

    /**
     * @type {boolean}
     */
    this.redemptionInProgress = false;

    /**
     * @type {boolean}
     */
    this.unredemptionInProgress = false;

    /**
     * @type {boolean}
     */
    this.redemptionError = false;

    /**
     * Item level field instances for this item instance.
     *
     * @type {FieldGroup}
     */
    this.fields = new FieldGroup({ level: 'item' });

    /**
     * The membershipDataset associated with this instance. Note: the membershipDataset can contain multiple members for
     * example a family membership.
     *
     * @type {MembershipDataset}
     */
    this.membershipDataset = null;

    /**
     * @type {SoldGiftVoucher}
     */
    this.soldGiftVoucher = null;

    /**
     * A unique identifier for this individual instance.
     *
     * This is used to identify an individual instance since it has no ID or other identifying info, and the lineNumber
     * and instance number are subject to change if the line this instance is on is split.
     *
     * @type {?String}
     */
    this.thirdPartyRef = crypto.randomUUID();

    /**
     * @type {SeatNumber[]}
     */
    this.seatNumbers = [];

    populate(this, properties);
};

ItemInstance.prototype = {
    /**
     * @return {object}
     */
    getHydrationMap() {
        return {
            fields: {
                model: FieldGroup,
            },
            ID: {
                field: ['ID', 'itemInstanceID'],
                transform: toNullableInt,
            },
            instance: toInt,
            itemInstanceRef: toNullableString,
            membershipDataset: {
                model: MembershipDataset,
            },
            membership: {
                field: (data) => {
                    const sub = data.membershipSubscription || data.membershipsubscriptions || null;
                    if (sub) {
                        const membership = sub.membership || sub.memberships || null;
                        if (membership) {
                            return membership;
                        }
                    }

                    return undefined;
                },
                model: Membership,
            },
            membershipSubscription: {
                field: ['membershipSubscription', 'membershipsubscriptions'],
                /**
                 * @param {any} value
                 * @param {{}} allValues
                 * @param {Hydrator} hydrator
                 *
                 * @return {?MembershipSubscription}
                 */
                transform(value, allValues, hydrator) {
                    if (!value) {
                        return null;
                    }
                    // TODO: Remove MembershipSubscription.itemInstance.

                    // Import MembershipSubscription here to avoid a circular dependency if it were imported up the top.
                    const MembershipSubscription = require('@/lib/Memberships/MembershipSubscription');
                    return hydrator.hydrate(value, new MembershipSubscription());
                },
            },
            netRedemptions: toNullableInt,
            orderLine: {
                field: ['orderLine', 'orderitems'],
                /**
                 * @param {any} value
                 * @param {{}} allValues
                 * @param {Hydrator} hydrator
                 *
                 * @return {?OrderLine}
                 */
                transform(value, allValues, hydrator) {
                    if (!value) {
                        return null;
                    }
                    // Import OrderLine here to avoid a circular dependency if it were imported up the top.
                    const OrderLine = require('./OrderLine');
                    return hydrator.hydrate(value, new OrderLine());
                },
            },
            redemptions: {
                modelCollection: Redemption,
            },
            seatNumbers: {
                field: ['seatNumbers', 'seatnumbers'],
                modelCollection: SeatNumber,
            },
            soldGiftVoucher: {
                model: SoldGiftVoucher,
            },
            thirdPartyRef: toNullableString,
        };
    },

    /**
     * @param {number} instance
     *
     * @return {ItemInstance}
     */
    setInstance(instance) {
        this.instance = instance;

        return this;
    },

    /**
     * @param {number|string} newCount
     */
    setNetRedemptions(newCount) {
        this.netRedemptions = parseInt(newCount, 10);
    },

    /**
     * @param {boolean} redemptionError
     */
    setRedemptionError(redemptionError) {
        this.clearRedemptionInProgress();
        this.redemptionError = redemptionError;
    },

    /**
     * @return {?Membership}
     */
    getMembership() {
        return this.membership;
    },

    /**
     * Get a list of Members on this item instance.
     *
     * @return {Member[]}
     */
    getMembers() {
        return this.membership ? this.membership.getMembers() : [];
    },

    setRedemptionInProgress() {
        this.redemptionInProgress = true;
        this.unredemptionInProgress = false;
    },

    setUnredemptionInProgress() {
        this.unredemptionInProgress = true;
        this.redemptionInProgress = false;
    },

    clearRedemptionInProgress() {
        this.redemptionInProgress = false;
        this.unredemptionInProgress = false;
    },

    /**
     * @return {boolean}
     */
    redemptionIsInProgress() {
        return this.redemptionInProgress || this.unredemptionInProgress;
    },

    /**
     * Regular item instance numbers start at 1.
     * Instance numbers when used in field data start at 0.
     * See BAC-2714
     *
     * @return {number}
     */
    get instanceForFieldData() {
        return this.instance - 1;
    },
};

module.exports = ItemInstance;
